import { useState, useCallback, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, useReducedMotion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { SITE } from "@/config/site";
import { api } from "@/api";
import logoUrl from "@/assets/ankhara-logo.webp";
import Modal from "@/components/Modal";
import JoinForm from "@/components/JoinForm";
import OpenCallForm from "@/components/OpenCallForm";
import ContactForm from "@/components/ContactForm";
import EmailPopup from "@/components/EmailPopup";
import Archive from "@/pages/Archive";
import Admin from "@/pages/Admin";
import Community from "@/pages/Community";
import Upload from "@/pages/Upload";
import FloorUploadRedirect from "@/pages/FloorUploadRedirect";

const track = (name, props = {}) => {
  try {
    window.posthog?.capture(name, props);
  } catch {
    /* posthog unavailable */
  }
  try {
    window.gtag?.("event", name, props);
  } catch {
    /* ga4 unavailable */
  }
};

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.085, delayChildren: 0.15 } },
};

const rise = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] } },
};

const fade = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

function Mask({ children, className = "" }) {
  return (
    <span className={`ank-mask ${className}`}>
      <motion.span className="ank-mask-inner" variants={rise}>
        {children}
      </motion.span>
    </span>
  );
}

function Marquee() {
  const unit = `${SITE.soundLine}  ✳  `.repeat(3);
  return (
    <div className="ank-marquee" aria-hidden="true">
      <div className="ank-marquee-track">
        <span>{unit}</span>
        <span>{unit}</span>
      </div>
    </div>
  );
}

function useCountdown(iso) {
  const [label, setLabel] = useState(null);
  useEffect(() => {
    if (!iso) return undefined;
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return undefined;
    const calc = () => {
      const days = Math.ceil((target - Date.now()) / 86400000);
      if (days < 0) setLabel(null);
      else if (days === 0) setLabel("TONIGHT — DOORS OPEN");
      else setLabel(`T—${days} DAY${days === 1 ? "" : "S"}`);
    };
    calc();
    const timer = setInterval(calc, 60000);
    return () => clearInterval(timer);
  }, [iso]);
  return label;
}

const sessionGaLabel = (title) => `sessions${(title || "").match(/\d{3}/)?.[0] || "current"}`;

// Calendar links are generated from the event's admin data (title, start/end ISO, venue, RSVP link).
// Dates are emitted in UTC, so end times rolling past midnight are handled by the clock, not by hand.
const calDate = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const eventEndIso = (ev) =>
  ev.endIso || new Date(new Date(ev.startIso).getTime() + 3 * 3600000).toISOString();

function gcalUrl(ev) {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: ev.title || "ANKHARA SESSIONS",
    dates: `${calDate(ev.startIso)}/${calDate(eventEndIso(ev))}`,
    location: ev.venue || "",
    details: [ev.blurb, ev.rsvpUrl ? `RSVP: ${ev.rsvpUrl}` : ""].filter(Boolean).join("\n"),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

const icsEscape = (s) =>
  String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");

function downloadIcs(ev) {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//ANKHARA//Sessions//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${Date.now()}@ankhara.world`,
    `DTSTAMP:${calDate(new Date().toISOString())}`,
    `DTSTART:${calDate(ev.startIso)}`,
    `DTEND:${calDate(eventEndIso(ev))}`,
    `SUMMARY:${icsEscape(ev.title)}`,
    `LOCATION:${icsEscape(ev.venue)}`,
    `DESCRIPTION:${icsEscape([ev.blurb, ev.rsvpUrl ? `RSVP: ${ev.rsvpUrl}` : ""].filter(Boolean).join("\n"))}`,
    ...(ev.rsvpUrl ? [`URL:${ev.rsvpUrl}`] : []),
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  const url = URL.createObjectURL(new Blob([lines.join("\r\n")], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${(ev.title || "ankhara-session").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  track("add_to_calendar", { method: "ics" });
}

function Landing() {
  const [modal, setModal] = useState(null); // 'about' | 'details' | 'join' | 'opencall' | 'contact'
  const close = useCallback(() => setModal(null), []);
  const reduceMotion = useReducedMotion();
  const [cfg, setCfg] = useState({ ...SITE, applicationsOpen: true });

  useEffect(() => {
    api
      .get("/site-config")
      .then(({ data }) => {
        setCfg((c) => ({
          ...c,
          applicationsOpen: data.applications_open,
          lineupAnnounced: data.lineup_announced,
          nextSessionOn: data.next_session_on,
          event: { ...c.event, ...(data.event || {}) },
          event2: { ...(c.event2 || {}), ...(data.event2 || {}) },
        }));
      })
      .catch(() => {});
  }, []);

  const [tab, setTab] = useState(0);
  const ev1 = cfg.event;
  const ev2 = cfg.event2 || {};
  // Second event (Session 005) is reachable via tabs; its RSVP link is the next-event
  // button URL so there is one source of truth. Clearing that URL hides the tab.
  const hasSecond = Boolean(ev1.nextRsvpUrl && ev2.title);
  const activeTab = tab === 1 && hasSecond ? 1 : 0;
  const ev = activeTab === 1
    ? { ...ev2, rsvpUrl: ev1.nextRsvpUrl, rsvpLabel: ev1.nextRsvpLabel || "RSVP" }
    : ev1;
  const cd1 = useCountdown(ev1.startIso);
  const cd2 = useCountdown(ev2.startIso);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 20 });
  const sy = useSpring(my, { stiffness: 40, damping: 20 });
  const bgX = useTransform(sx, (v) => v * -12);
  const bgY = useTransform(sy, (v) => v * -8);

  const onPointerMove = useCallback(
    (e) => {
      if (reduceMotion || !window.matchMedia("(pointer: fine)").matches) return;
      mx.set(e.clientX / window.innerWidth - 0.5);
      my.set(e.clientY / window.innerHeight - 0.5);
    },
    [mx, my, reduceMotion]
  );

  return (
    <div className="ank-shell" data-testid="landing-shell" onPointerMove={onPointerMove}>
      <motion.div className="ank-parallax" style={reduceMotion ? undefined : { x: bgX, y: bgY }}>
        <div
          className="ank-bg"
          role="img"
          aria-label="Handmade collage of two dancers moving together among green botanicals, vinyl records and paper texture"
        />
      </motion.div>
      <div className="ank-overlay" aria-hidden="true" />
      <div className="ank-grain" aria-hidden="true" />

      <motion.div
        className="ank-frame"
        variants={container}
        initial={reduceMotion ? "show" : "hidden"}
        animate="show"
      >
        <motion.div variants={fade}>
          <Marquee />
        </motion.div>

        <header className="ank-top">
          <div className="ank-brand">
            <h1 className="ank-wordmark" data-testid="wordmark" aria-label={SITE.name}>
              <Mask>
                <img src={logoUrl} alt="" className="ank-logo-img" />
              </Mask>
            </h1>
            <p className="ank-tagline" data-testid="tagline">
              <Mask>{SITE.tagline}</Mask>
            </p>
          </div>
          <motion.nav className="ank-nav" aria-label="Site" variants={fade}>
            <a
              href="/archive"
              className="ank-nav-link"
              data-testid="archive-nav-link"
              onClick={() => track("archive_nav_click")}
            >
              ARCHIVE
            </a>
            <a
              href="/community"
              className="ank-nav-link"
              data-testid="community-nav-link"
              onClick={() => track("community_nav_click")}
            >
              THE FEED
            </a>
            <a
              href={SITE.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ank-nav-link"
              data-testid="instagram-link"
              onClick={() => track("instagram_click")}
            >
              INSTAGRAM ↗
            </a>
            <button
              type="button"
              className="ank-nav-link"
              data-testid="about-open-btn"
              onClick={() => { track("about_modal_open"); setModal("about"); }}
            >
              ABOUT
            </button>
          </motion.nav>
        </header>

        <main className="ank-event" data-testid="event-panel">
          <p className="ank-soundline" data-testid="sound-line">
            <Mask>{SITE.soundLine}</Mask>
          </p>
          {hasSecond && (
            <div className="ank-evtabs" data-testid="event-tabs" role="tablist" aria-label="Choose session">
              {[ev1, { ...ev2, rsvpLabel: ev1.nextRsvpLabel }].map((e, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === i}
                  className={`ank-evtab ${activeTab === i ? "is-active" : ""}`}
                  data-testid={`event-tab-${i}`}
                  onClick={() => { setTab(i); track("event_tab_switch", { tab: i, event: e.title }); }}
                >
                  {(e.title || "").replace("ANKHARA SESSIONS: ", "SESSION ")}
                </button>
              ))}
            </div>
          )}
          {/* Both sessions render stacked in one grid cell: the panel is always as tall as the
              taller session, so toggling tabs never shifts the buttons below. */}
          <div className="ank-evstack" data-testid="event-stack">
            {(hasSecond
              ? [ev1, { ...ev2, rsvpUrl: ev1.nextRsvpUrl, rsvpLabel: ev1.nextRsvpLabel || "RSVP" }]
              : [ev1]
            ).map((p, i) => {
              const on = i === activeTab;
              const cd = i === 1 ? cd2 : cd1;
              return (
                <div
                  key={i}
                  className={`ank-evpane ${on ? "is-active" : ""}`}
                  aria-hidden={!on}
                  data-testid={`event-pane-${i}`}
                >
                  <h2 className="ank-event-title" data-testid={on ? "event-title" : undefined}>
                    <Mask>{p.title}</Mask>
                  </h2>
                  <ul className="ank-event-meta" data-testid={on ? "event-meta" : undefined}>
                    {[p.date, p.time, p.venue, `${p.age} · ${p.entry}`].filter(Boolean).map((line) => (
                      <li key={line}>
                        <Mask>{line}</Mask>
                      </li>
                    ))}
                  </ul>
                  {cd && (
                    <p className="ank-countdown" data-testid={on ? "countdown" : undefined}>
                      <Mask>{cd}</Mask>
                    </p>
                  )}
                  {cfg.lineupAnnounced && p.lineup && (
                    <p className="ank-lineup" data-testid={on ? "lineup-teaser" : undefined}>
                      <Mask>LINEUP — {p.lineup}</Mask>
                    </p>
                  )}
                  <p className="ank-event-blurb" data-testid={on ? "event-blurb" : undefined}>
                    <Mask>{p.blurb}</Mask>
                  </p>
                  <p className="ank-description" data-testid={on ? "series-description" : undefined}>
                    <Mask>{SITE.description}</Mask>
                  </p>
                  {cfg.nextSessionOn && p.nextSession && (
                    <p className="ank-nextline" data-testid={on ? "next-session-teaser" : undefined}>
                      <Mask>NEXT TRANSMISSION — {p.nextSession}</Mask>
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          <motion.div className="ank-actions" data-testid="actions" variants={fade}>
            <a
              href={ev.rsvpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ank-rsvp"
              data-testid="rsvp-btn"
              onClick={() => track("rsvp_click", { event_category: "engagement", event_label: sessionGaLabel(ev.title), event: ev.title })}
            >
              {ev.rsvpLabel || "RSVP"}
            </a>
            <div className="ank-secondary">
              <button
                type="button"
                className="ank-stamp ank-stamp-a"
                data-testid="join-open-btn"
                onClick={() => { track("join_modal_open"); setModal("join"); }}
              >
                JOIN THE COMMUNITY
              </button>
              <button
                type="button"
                className="ank-stamp ank-stamp-b"
                data-testid="opencall-open-btn"
                onClick={() => { track("opencall_modal_open"); setModal("opencall"); }}
              >
                OPEN CALLS
              </button>
              <button
                type="button"
                className="ank-details-link"
                data-testid="details-open-btn"
                onClick={() => { track("session_details_open"); setModal("details"); }}
              >
                SESSION DETAILS →
              </button>
            </div>
          </motion.div>
        </main>
      </motion.div>

      {/* Desktop only: Session 005 floats as an "UP NEXT" ticket card on the artwork.
          Mobile uses the tab switcher inside the panel instead (card hidden via CSS). */}
      {hasSecond && (
        <aside className="ank-nextcard" data-testid="upnext-card">
          <p className="ank-nextcard-kicker">UP NEXT</p>
          <h3 className="ank-nextcard-title" data-testid="upnext-title">{ev2.title}</h3>
          <p className="ank-nextcard-meta" data-testid="upnext-meta">
            {ev2.date}<br />{ev2.time}<br />{ev2.venue}
          </p>
          {cd2 && (
            <p className="ank-nextcard-count" data-testid="upnext-countdown">{cd2}</p>
          )}
          {cfg.lineupAnnounced && ev2.lineup && (
            <p className="ank-nextcard-lineup" data-testid="upnext-lineup">LINEUP — {ev2.lineup}</p>
          )}
          <a
            href={ev1.nextRsvpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="ank-nextcard-rsvp"
            data-testid="upnext-rsvp"
            onClick={() => track("rsvp_click", { event_category: "engagement", event_label: "sessions005", event: ev1.nextRsvpLabel, source: "upnext_card" })}
          >
            {ev1.nextRsvpLabel || "RSVP"} ↗
          </a>
        </aside>
      )}

      {modal === "about" && (
        <Modal label="About ANKHARA" onClose={close} testId="about-modal">
          <h3 className="ank-modal-title">ABOUT ANKHARA</h3>
          {SITE.about.split("\n\n").map((p, i) => (
            <div key={i} className="ank-chapter">
              <span className="ank-chapter-num" aria-hidden="true">
                {String(i + 1).padStart(2, "0")} —
              </span>
              <p className="ank-modal-copy" data-testid={`about-copy-${i}`}>{p}</p>
            </div>
          ))}
          <button
            type="button"
            className="ank-details-link ank-contact-link"
            data-testid="contact-open-btn"
            onClick={() => setModal("contact")}
          >
            CONTACT ANKHARA →
          </button>
        </Modal>
      )}

      {modal === "details" && (
        <Modal label="Session details" onClose={close} testId="details-modal">
          <h3 className="ank-modal-title" data-testid="details-title">{ev.title}</h3>
          <p className="ank-modal-copy">{ev.blurb}</p>
          <dl className="ank-detail-list">
            <div><dt>DATE</dt><dd>{ev.date}</dd></div>
            <div><dt>TIME</dt><dd>{ev.time}</dd></div>
            <div><dt>VENUE</dt><dd>{ev.venue}</dd></div>
            <div><dt>ENTRY</dt><dd>{ev.age} · {ev.entry}</dd></div>
            <div><dt>SOUNDS</dt><dd>Brazilian funk, Afrobeats, dancehall, GQOM, Jersey club, reggaeton, amapiano, soca, bouyon, electronic and the global underground</dd></div>
            <div><dt>LINEUP</dt><dd>{ev.lineup || "Eight DJs, thirty-minute sets. Full lineup announced via the root system mailing list."}</dd></div>
            <div><dt>ACCESS</dt><dd>{ev.access || "For accessibility questions or accommodations, reach out before the session and we will make it work."}</dd></div>
          </dl>
          <div className="ank-modal-actions">
            <a
              href={ev.rsvpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ank-rsvp ank-rsvp-modal"
              data-testid="details-rsvp-btn"
              onClick={() => track("rsvp_click", { event_category: "engagement", event_label: sessionGaLabel(ev.title), event: ev.title, source: "details_modal" })}
            >
              {ev.rsvpLabel || "RSVP"}
            </a>
            {ev.startIso && (
              <>
                <a
                  href={gcalUrl(ev)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ank-cal-link"
                  data-testid="add-to-calendar-link"
                  onClick={() => track("add_to_calendar", { method: "google" })}
                >
                  ADD TO CALENDAR ↗
                </a>
                <button
                  type="button"
                  className="ank-cal-link"
                  data-testid="add-to-calendar-ics"
                  onClick={() => downloadIcs(ev)}
                >
                  .ICS DOWNLOAD ↓
                </button>
              </>
            )}
          </div>
        </Modal>
      )}

      {modal === "join" && (
        <Modal label="Join the community" onClose={close} testId="join-modal">
          <h3 className="ank-modal-title" data-testid="join-title">{SITE.join.heading}</h3>
          <p className="ank-modal-copy" data-testid="join-copy">{SITE.join.copy}</p>
          <JoinForm />
        </Modal>
      )}

      {modal === "opencall" && (
        <Modal label="Open calls" onClose={close} testId="opencall-modal" wide>
          <h3 className="ank-modal-title" data-testid="opencall-title">{SITE.openCall.heading}</h3>
          <p className="ank-modal-copy" data-testid="opencall-copy">{SITE.openCall.copy}</p>
          <OpenCallForm applicationsOpen={cfg.applicationsOpen} />
        </Modal>
      )}

      {modal === "contact" && (
        <Modal label="Contact ANKHARA" onClose={close} testId="contact-modal">
          <h3 className="ank-modal-title" data-testid="contact-title">CONTACT ANKHARA</h3>
          <ContactForm />
        </Modal>
      )}

      <EmailPopup />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/archive" element={<Archive />} />
        <Route path="/community" element={<Community />} />
        <Route path="/community/upload" element={<FloorUploadRedirect />} />
        <Route path="/upload/:token" element={<Upload />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
